import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { GnbValidacionModalService } from '../../../core/services/conf/gnb-validacion-modal.service';
import { GnbService, GnbMovementsReportDTO, GnbMovementDTO } from '../../../core/services/api/gnb.service';

@Component({
    selector: 'app-gnb-validacion-modal',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, DialogModule, InputTextModule, DatePickerModule, TagModule, ToastModule, TooltipModule],
    providers: [MessageService],
    templateUrl: './gnb-validacion-modal.component.html'
})
export class GnbValidacionModalComponent implements OnInit, OnDestroy {
    private gnbValidacionModalService = inject(GnbValidacionModalService);
    private gnbService = inject(GnbService);
    private messageService = inject(MessageService);
    private subscription?: Subscription;

    @ViewChild('clienteInput') clienteInput?: ElementRef<HTMLInputElement>;

    visible = false;
    loading = false;
    buscado = false;

    desde: Date | null = null;
    hasta: Date | null = null;
    clienteEmisor = '';
    montoTexto = '';
    report: GnbMovementsReportDTO | null = null;

    ngOnInit(): void {
        this.subscription = this.gnbValidacionModalService.open$.subscribe(() => this.openModal());
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    openModal(): void {
        this.buscado = false;
        this.setRangoInicial();
        this.clienteEmisor = '';
        this.montoTexto = '';
        this.report = null;
        this.visible = true;
    }

    onShow(): void {
        setTimeout(() => this.clienteInput?.nativeElement.focus(), 0);
    }

    private setRangoInicial(): void {
        const now = new Date();
        this.desde = new Date(now.getTime() - 60 * 60 * 1000);
        this.hasta = now;
    }

    nuevaBusqueda(): void {
        this.buscado = false;
        this.setRangoInicial();
        this.clienteEmisor = '';
        this.montoTexto = '';
        this.report = null;
        this.onShow();
    }

    get monto(): number | null {
        if (!this.montoTexto) return null;
        const cleaned = this.montoTexto.replace(/\./g, '').trim();
        if (!cleaned) return null;
        const value = Number(cleaned);
        return isNaN(value) ? null : value;
    }

    formatMonto(): void {
        const digits = this.montoTexto.replace(/\D/g, '');
        if (!digits) {
            this.montoTexto = '';
            return;
        }
        this.montoTexto = digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    validar(): void {
        if (this.monto == null) {
            this.messageService.add({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese el monto a validar' });
            return;
        }
        if (!this.desde || !this.hasta) {
            this.messageService.add({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione el rango de fecha y hora' });
            return;
        }
        this.loading = true;
        this.report = null;
        this.gnbService
            .getMovementsByRange({
                concept: 'SPI',
                clienteEmisor: this.clienteEmisor.trim() || undefined,
                monto: this.monto,
                cdtDbtInd: 'CRDT',
                fromDate: this.fmtDate(this.desde),
                fromTime: this.fmtTime(this.desde),
                toDate: this.fmtDate(this.hasta),
                toTime: this.fmtTime(this.hasta)
            })
            .subscribe({
                next: (res) => {
                    this.report = res.object ?? null;
                    this.buscado = true;
                    this.loading = false;
                },
                error: () => {
                    this.loading = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudieron validar las transacciones' });
                }
            });
    }

    get transacciones(): GnbMovementDTO[] {
        return this.report?.movements ?? [];
    }

    statusSeverity(status?: string): 'success' | 'warn' | 'danger' {
        if (status === 'FOUND') return 'success';
        if (status === 'PENDING') return 'warn';
        return 'danger';
    }

    statusLabel(status?: string): string {
        if (status === 'FOUND') return 'Encontrado';
        if (status === 'PENDING') return 'Pendiente';
        return 'Error técnico';
    }

    statusClass(status?: string): string {
        if (status === 'FOUND') return 'bg-green-50 dark:bg-green-900/20 border border-green-300';
        if (status === 'PENDING') return 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300';
        return 'bg-red-50 dark:bg-red-900/20 border border-red-300';
    }

    statusSeveritySts(sts?: string): 'success' | 'warn' | 'danger' | 'secondary' {
        if (sts === 'ACCP' || sts === 'ACSP') return 'success';
        if (sts === 'RJCT') return 'danger';
        if (sts === 'PART' || sts === 'PDNG') return 'warn';
        return 'secondary';
    }

    async copiarEqSeq(eqSeq?: string): Promise<void> {
        if (!eqSeq) return;
        try {
            await navigator.clipboard.writeText(eqSeq);
            this.messageService.add({ severity: 'success', summary: 'Copiado', detail: 'Nro de Transacción copiado' });
        } catch {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo copiar el Nro de Transacción' });
        }
    }

    private fmtDate(d: Date): string {
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }

    private fmtTime(d: Date): string {
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
    }
}